import { Badge } from "@repo/ui/components/ui/badge";
import type {
  SubscriptionConfig,
  SubscriptionPlanTier,
} from "@repo/ui/lib/api/services/settings.service";
import { cn } from "@repo/ui/lib/utils";
import { Check, Sparkles, TreePine, Zap } from "lucide-react";

interface PlanSelectionProps {
  subscriptionConfig: SubscriptionConfig;
  selectedPlanType: "BASIC" | "ADVANCED";
  selectedTierId: string;
  onPlanSelect: (planType: "BASIC" | "ADVANCED", tierId: string) => void;
}

/** Index of the tier that gets the "Recommandé" badge (middle-ish) */
function getRecommendedIndex(tiers: SubscriptionPlanTier[]): number {
  if (tiers.length <= 1) return 0;
  return Math.floor((tiers.length - 1) / 2);
}

export function PlanSelection({
  subscriptionConfig,
  selectedPlanType,
  selectedTierId,
  onPlanSelect,
}: PlanSelectionProps) {
  const basicPlan = subscriptionConfig?.plans?.BASIC;
  const advancedPlan = subscriptionConfig?.plans?.ADVANCED;

  const activePlan = selectedPlanType === "BASIC" ? basicPlan : advancedPlan;
  const activeTiers = activePlan?.tiers ?? [];

  const selectedTier = activeTiers.find((t) => t.id === selectedTierId) ?? null;
  const recommendedIndex = getRecommendedIndex(activeTiers);

  const switchPlan = (planType: "BASIC" | "ADVANCED") => {
    const plan = planType === "BASIC" ? basicPlan : advancedPlan;
    const firstTier = plan?.tiers?.[0];
    onPlanSelect(planType, firstTier?.id ?? "");
  };

  return (
    <div className="space-y-5">
      {/* ── 1. Plan type switcher ─────────────────────────────── */}
      <div className="inline-flex w-full rounded-xl bg-muted p-1 gap-1">
        {(["BASIC", "ADVANCED"] as const).map((planType) => {
          const isActive = selectedPlanType === planType;
          return (
            <button
              key={planType}
              type="button"
              onClick={() => switchPlan(planType)}
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-150 select-none",
                isActive
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-background/50"
              )}
            >
              {planType === "BASIC" ? (
                <Zap className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 shrink-0" />
              )}
              <span>
                {planType === "BASIC" ? "Essentiel" : "Avancé"}
              </span>
              {planType === "ADVANCED" && (
                <span className="text-[10px] font-semibold bg-primary/15 text-primary px-1.5 py-0.5 rounded-full leading-none">
                  Plus
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── 2. Tier grid ──────────────────────────────────────── */}
      {activeTiers.length > 0 ? (
        <div
          className={cn(
            "grid gap-3",
            activeTiers.length <= 2
              ? "grid-cols-2"
              : activeTiers.length === 3
                ? "grid-cols-3"
                : "grid-cols-2 sm:grid-cols-4"
          )}
        >
          {activeTiers.map((tier, index) => {
            const isSelected = tier.id === selectedTierId;
            const isRecommended = index === recommendedIndex;

            return (
              <button
                key={tier.id}
                type="button"
                onClick={() => onPlanSelect(selectedPlanType, tier.id)}
                className={cn(
                  "relative flex flex-col items-center rounded-xl border-2 px-3 py-4 text-center transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                  isSelected
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border/60 bg-card hover:border-primary/30 hover:bg-primary/[0.03]"
                )}
              >
                {/* Recommended badge */}
                {isRecommended && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold text-primary-foreground shadow-sm">
                    Recommandé
                  </span>
                )}

                {/* Tree count */}
                <div className="mt-1 flex items-end gap-1 leading-none">
                  <span
                    className={cn(
                      "text-2xl font-bold tabular-nums transition-colors",
                      isSelected ? "text-primary" : "text-foreground"
                    )}
                  >
                    {tier.maxTrees.toLocaleString("fr-FR")}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                  <TreePine className="h-3 w-3" />
                  <span>arbres/mois</span>
                </div>

                {/* Divider */}
                <div className="my-3 h-px w-full bg-border/50" />

                {/* Price */}
                <div className="flex items-end gap-0.5 leading-none">
                  <span
                    className={cn(
                      "text-xl font-bold tabular-nums transition-colors",
                      isSelected ? "text-primary" : "text-foreground"
                    )}
                  >
                    {tier.price}€
                  </span>
                </div>
                <span className="mt-0.5 text-[11px] text-muted-foreground">
                  /mois
                </span>

                {/* Selected indicator */}
                <div
                  className={cn(
                    "mt-3 flex h-5 w-5 items-center justify-center rounded-full transition-all duration-150",
                    isSelected
                      ? "bg-primary opacity-100 scale-100"
                      : "opacity-0 scale-75"
                  )}
                >
                  <Check className="h-3 w-3 text-primary-foreground" />
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="py-4 text-center text-sm text-muted-foreground">
          Aucun forfait disponible pour ce plan.
        </p>
      )}

      {/* ── 3. Summary strip ─────────────────────────────────── */}
      {selectedTier && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 animate-in fade-in-0 duration-200">
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary">
            <Check className="h-3 w-3 text-primary-foreground" />
          </div>
          <p className="text-sm text-foreground">
            <span className="font-medium">Plan sélectionné</span>
            <span className="mx-1.5 text-muted-foreground">·</span>
            <span className="text-muted-foreground">
              {selectedPlanType === "BASIC" ? "Essentiel" : "Avancé"}
            </span>
            <span className="mx-1.5 text-muted-foreground">·</span>
            <span className="text-muted-foreground">
              {selectedTier.maxTrees.toLocaleString("fr-FR")} arbres/mois
            </span>
            <span className="mx-1.5 text-muted-foreground">·</span>
            <span className="font-semibold text-primary">
              {selectedTier.price}€/mois
            </span>
          </p>
        </div>
      )}
    </div>
  );
}
